import React, { useState, useEffect } from 'react';
import { FiEdit, FiUser, FiMail, FiMapPin, FiPhone } from 'react-icons/fi';
import { FaRegBuilding } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../app/store';
import {
  fetchBillingProfile2,
  updateBillingProfile2,
  updateBillingDetails2,
  updateContactDetails2,
  updateLabelReceiptsAsInvoices2,
  updateBillEstimates2,
  clearBillingError2,
  SectionData
} from '../../features/billing/billingSlice2';
// @ts-ignore: CSS side-effect import without type declarations
import 'react-toastify/dist/ReactToastify.css';

// Icon components with type casting
const FiEditComponent = FiEdit as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiUserComponent = FiUser as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FaRegBuildingComponent = FaRegBuilding as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiMailComponent = FiMail as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiMapPinComponent = FiMapPin as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiPhoneComponent = FiPhone as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

interface EditableSectionProps {
  title: string;
  fields: (keyof SectionData)[];
  data: SectionData;
  onSave: (updated: SectionData) => void;
  isEditing: boolean;
  onEditToggle: () => void;
  onCancel: () => void;
}

// Editable Section Component
const EditableSection: React.FC<EditableSectionProps> = ({
  title,
  fields,
  data,
  onSave,
  isEditing,
  onEditToggle,
  onCancel,
}) => {
  const [formData, setFormData] = useState<SectionData>(data);
  const [initialData, setInitialData] = useState<SectionData>(data);
  const [errors, setErrors] = useState<Partial<Record<keyof SectionData, string>>>({});
  const [isChanged, setIsChanged] = useState(false);

  useEffect(() => {
    setFormData(data);
    setInitialData(data);
    setIsChanged(false);
    setErrors({});
  }, [data]);

  useEffect(() => {
    if (!isEditing) {
      setFormData(initialData);
      setIsChanged(false);
      setErrors({});
    }
  }, [isEditing, initialData]);

  const handleChange = (field: keyof SectionData, value: string) => {
    setFormData({ ...formData, [field]: value });
    if (value.trim() !== '') {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
    const hasChanged = fields.some(f => formData[f] !== initialData[f]);
    setIsChanged(hasChanged);
  };

  const handleUpdate = () => {
    const newErrors: Partial<Record<keyof SectionData, string>> = {};
    fields.forEach((f) => {
      if (!formData[f] || formData[f]?.trim() === '') {
        newErrors[f] = `${f.toUpperCase()} is required`;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSave(formData);
  };

  const handleCancel = () => {
    setFormData(initialData);
    setErrors({});
    setIsChanged(false);
    onCancel();
  };

  const getIcon = (field: keyof SectionData) => {
    switch (field) {
      case 'name':
        return <FiUserComponent size={17} />;
      case 'company':
        return <FaRegBuildingComponent size={17} />;
      case 'email':
        return <FiMailComponent size={17} />;
      case 'phone':
        return <FiPhoneComponent size={17} />;
      case 'address':
        return <FiMapPinComponent size={17} />;
      default:
        return null;
    }
  };

  return (
    <div className='border border-[#E5E9EC] rounded-lg bg-white p-5'>
      <div className='flex justify-between items-center mb-6'>
        <h3 className='text-[#3F5265] font-semibold text-[18px]'>{title}</h3>
        <div className='flex items-center gap-2'>
          {isEditing && (
            <button
              onClick={handleCancel}
              className='flex items-center gap-1 border rounded-sm px-3 py-2 text-sm font-medium border-[#00A4BD] text-[#00A4BD]'
            >
              Cancel
            </button>
          )}

          <button
            onClick={isEditing ? handleUpdate : onEditToggle}
            disabled={isEditing && !isChanged}
            className={`flex items-center gap-1 border rounded-sm px-3 py-2 text-sm font-medium 
            ${isEditing
                ? isChanged
                  ? 'border-[#00A4BD] text-[#00A4BD]'
                  : 'border-gray-300 text-gray-400 cursor-not-allowed'
                : 'border-[#00A4BD] text-[#00A4BD]'
              }`}
          >
            <FiEditComponent /> {isEditing ? 'Update' : 'Edit'}
          </button>
        </div>
      </div>
      <ul className='space-y-4'>
        {fields.map((field) => (
          <li key={field} className='flex flex-col'>
            <div className='flex items-center'>
              <span className='flex items-center text-[#9FA9B2] text-[12px] gap-[5px] mr-[12px]'>
                <span className='flex items-center justify-center mr-2 rounded-[5px] w-[30px] h-[30px] bg-[#F5F8FA] text-[#9FA9B2]'>
                  {getIcon(field)}
                </span>
                {field.toUpperCase()}
                {isEditing ? (
                  <input
                    type='text'
                    value={formData[field] || ''}
                    onChange={(e) => handleChange(field, e.target.value)}
                    className='ml-2 border border-gray-300 rounded px-2 py-1 text-[14px] text-[#3F5265] flex-1'
                  />
                ) : (
                  <span className='text-[#3F5265] text-[14px] -mt-[1.9px] ml-2'>
                    {formData[field] || 'Not set'}
                  </span>
                )}
              </span>
            </div>
            {errors[field] && (
              <p className='text-red-500 text-xs ml-[45px] mt-1'>
                {errors[field]}
              </p>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

// Main Component
const BillingInformation: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  // Get state from Redux
  const {
    billingDetails,
    contactDetails,
    labelReceiptsAsInvoices,
    billEstimates,
    loading,
    // updating,
    error,
  } = useSelector((state: RootState) => state.billing2);

  // CORRECTED: useState with proper TypeScript generic syntax
  const [editingSection, setEditingSection] = useState<'billing' | 'contact' | null>(null);

  useEffect(() => {
    dispatch(fetchBillingProfile2());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearBillingError2());
    }
  }, [error, dispatch]);

  const handleSaveBillingDetails = (updated: SectionData) => {
    dispatch(updateBillingDetails2(updated));
    dispatch(updateBillingProfile2()).then((result) => {
      if (result.meta.requestStatus === 'fulfilled') {
        toast.success('Billing details updated successfully!');
        setEditingSection(null);
      }
    });
  };

  const handleSaveContactDetails = (updated: SectionData) => {
    dispatch(updateContactDetails2(updated));
    dispatch(updateBillingProfile2()).then((result) => {
      if (result.meta.requestStatus === 'fulfilled') {
        toast.success('Contact details updated successfully!');
        setEditingSection(null);
      }
    });
  };

  const handleCheckboxChange = (type: 'labelReceiptsAsInvoices' | 'billEstimates', value: boolean) => {
    if (type === 'labelReceiptsAsInvoices') {
      dispatch(updateLabelReceiptsAsInvoices2(value));
    } else {
      dispatch(updateBillEstimates2(value));
    }
    dispatch(updateBillingProfile2()).then((result) => {
      if (result.meta.requestStatus === 'fulfilled') {
        toast.success('Billing preferences updated successfully!');
      }
    });
  };

  const handleEditToggle = (section: 'billing' | 'contact') => {
    if (editingSection === section) return;
    setEditingSection(null);
    setTimeout(() => {
      setEditingSection(section);
    }, 50);
  };

  const handleCancel = () => {
    setEditingSection(null);
  };

  if (loading) {
    return (
      <div className='flex justify-center items-center py-12'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-[#00899E]'></div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
        <EditableSection
          title='Billing Details'
          fields={['name', 'company', 'email', 'address']}
          data={billingDetails}
          onSave={handleSaveBillingDetails}
          isEditing={editingSection === 'billing'}
          onEditToggle={() => handleEditToggle('billing')}
          onCancel={handleCancel}
        />
        <EditableSection
          title='Primary Account Contact'
          fields={['name', 'company', 'email', 'phone', 'address']}
          data={contactDetails}
          onSave={handleSaveContactDetails}
          isEditing={editingSection === 'contact'}
          onEditToggle={() => handleEditToggle('contact')}
          onCancel={handleCancel}
        />
      </div>

      <div className='border border-[#E5E9EC] rounded-lg bg-white p-5'>
        <h3 className='text-[#3F5265] font-semibold mb-2 text-[16px]'>
          Notifications
        </h3>
        <p className='text-[#657584] text-[16px] mb-4'>
          Receipts and billing alerts will be sent to{' '}
          <span className='font-medium'>{billingDetails?.email || 'Not set'}</span>
        </p>

        <div className='space-y-4 bg-[#F5F8FA] p-5 rounded-lg'>
          <label className='flex items-start gap-3 cursor-pointer'>
            <input
              type='checkbox'
              checked={labelReceiptsAsInvoices}
              onChange={(e) => handleCheckboxChange('labelReceiptsAsInvoices', e.target.checked)}
              className={`relative h-5 w-5 appearance-none rounded-md border-gray-400 border-[2px]
                          after:absolute after:inset-0 after:flex after:items-center after:justify-center
                          checked:bg-[#00A4BD] checked:border-[#00A4BD] disabled:bg-gray-400 disabled:border-gray-400
                          checked:after:content-['✓'] checked:after:text-white checked:after:font-bold checked:after:text-sm`}
            />
            <span>
              <span className='block font-medium text-[#3F5265] text-[16px] mb-1'>
                Label receipts as invoices
              </span>
              <span className='text-[#657584] text-[14px] leading-relaxed'>
                Select this option to label your receipt header as "Invoice" for
                legal or accounting reasons. This will not affect how you are
                billed.
              </span>
            </span>
          </label>

          <label className='flex items-start gap-3 cursor-pointer'>
            <input
              type='checkbox'
              checked={billEstimates}
              onChange={(e) => handleCheckboxChange('billEstimates', e.target.checked)}
              className={`relative h-5 w-5 appearance-none rounded-md border-gray-400 border-[2px]
                          after:absolute after:inset-0 after:flex after:items-center after:justify-center
                          checked:bg-[#00A4BD] checked:border-[#00A4BD] disabled:bg-gray-400 disabled:border-gray-400
                          checked:after:content-['✓'] checked:after:text-white checked:after:font-bold checked:after:text-sm`}
            />
            <span>
              <span className='block font-medium text-[#3F5265] text-[16px] mb-1'>
                Bill estimates
              </span>
              <span className='text-[#657584] text-[14px] leading-relaxed'>
                Received 2 weeks before recurring charges
              </span>
            </span>
          </label>
        </div>
      </div>
    </div>
  );
};

export default BillingInformation;